import React, { useState, useRef, useEffect } from "react";
import "./TabLockScreen.css";

const CORRECT_CODE = "223015";
const CODE_LENGTH  = 6;

/**
 * Props:
 *   tabLabel  {string}    — heading shown on the lock card
 *   children  {node}      — content revealed after unlock (tab-overlay mode)
 *   modal     {boolean}   — if true, renders as a fixed full-viewport overlay
 *                           with a dark backdrop; children are NOT rendered —
 *                           use onUnlock to react to success instead
 *   onUnlock  {function}  — called (no args) when the correct code is entered
 *                           (useful in modal mode so the caller can take action)
 *   onClose   {function}  — called when the user dismisses the modal (backdrop click)
 */
const TabLockScreen = ({ tabLabel, children, modal = false, onUnlock, onClose }) => {
    const [digits, setDigits]   = useState(Array(CODE_LENGTH).fill(""));
    const [shake, setShake]     = useState(false);
    const [error, setError]     = useState("");
    const [unlocked, setUnlocked] = useState(false);
    const inputRefs = useRef([]);

    // Focus the first box on mount
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);
    
    const attempt = (code) => {
        if (code === CORRECT_CODE) {
            setUnlocked(true);
            onUnlock?.();
        } else {
            setError("Incorrect passcode.");
            setShake(true);
            const cleared = Array(CODE_LENGTH).fill("");
            setDigits(cleared);
            setTimeout(() => {
                setShake(false);
                inputRefs.current[0]?.focus();
            }, 600);
        }
    };
    
    const handleChange = (idx, e) => {
        const val = e.target.value.replace(/\D/g, ""); // digits only
        if (!val) return;

        // Accept only the last digit typed (handles paste of one char)
        const digit = val.slice(-1);
        const next  = digits.map((d, i) => (i === idx ? digit : d));
        setDigits(next);
        setError("");

        if (idx < CODE_LENGTH - 1) {
            // Advance focus
            inputRefs.current[idx + 1]?.focus();
        } else {
            // Last box filled — attempt immediately
            const code = next.join("");
            if (code.length === CODE_LENGTH) attempt(code);
        }
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === "Backspace") {
            e.preventDefault();
            if (digits[idx]) {
                // Clear current box
                setDigits((prev) => prev.map((d, i) => (i === idx ? "" : d)));
                setError("");
            } else if (idx > 0) {
                // Move back and clear previous box
                const prev = digits.map((d, i) => (i === idx - 1 ? "" : d));
                setDigits(prev);
                setError("");
                inputRefs.current[idx - 1]?.focus();
            }
        } else if (e.key === "ArrowLeft" && idx > 0) {
            inputRefs.current[idx - 1]?.focus();
        } else if (e.key === "ArrowRight" && idx < CODE_LENGTH - 1) {
            inputRefs.current[idx + 1]?.focus();
        }
    };

    // Support pasting a full 6-digit code into any box
    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
        if (!pasted) return;
        const next = Array(CODE_LENGTH).fill("").map((_, i) => pasted[i] || "");
        setDigits(next);
        setError("");
        const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
        inputRefs.current[focusIdx]?.focus();
        if (pasted.length === CODE_LENGTH) attempt(pasted);
    };

    // Tab-overlay mode: once unlocked, render children directly
    if (!modal && unlocked) return <>{children}</>;

    // Modal mode: once unlocked the parent handles everything — just disappear
    if (modal && unlocked) return null;

    const overlayClass = `tab-lock-overlay${modal ? " tab-lock-overlay--modal" : ""}`;

    return (
        <div
            className={overlayClass}
            onClick={modal ? (e) => { if (e.target === e.currentTarget) onClose?.(); } : undefined}
        >
            <div className={`tab-lock-card ${shake ? "tab-lock-card--shake" : ""}`}>
                <div className="tab-lock-icon">🔒</div>
                <h2 className="tab-lock-title">{tabLabel}</h2>
                <p className="tab-lock-subtitle">
                    This feature is temporarily restricted.<br />
                    Enter the access passcode to continue.
                </p>

                {/* 6-box digit input */}
                <div className="tab-lock-digits" onPaste={handlePaste}>
                    {digits.map((d, idx) => (
                        <input
                            key={idx}
                            ref={(el) => (inputRefs.current[idx] = el)}
                            className={`tab-lock-digit ${d ? "tab-lock-digit--filled" : ""}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={d}
                            onChange={(e) => handleChange(idx, e)}
                            onKeyDown={(e) => handleKeyDown(idx, e)}
                            // Prevent ALL password managers / browser autofill popups:
                            // - autoComplete="one-time-code" → browsers treat as OTP, skip saved-password overlay
                            // - type="text" (not "password") → doesn't trigger the password-autofill heuristic
                            // - CSS -webkit-text-security: disc handles visual masking instead
                            // - data-* attrs silence the most common password manager extensions
                            autoComplete="one-time-code"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            data-form-type="other"
                        />
                    ))}
                </div>

                {error && <p className="tab-lock-error">{error}</p>}

                <button
                    className="tab-lock-btn"
                    onClick={() => attempt(digits.join(""))}
                    disabled={digits.join("").length < CODE_LENGTH}
                >
                    Unlock
                </button>
            </div>
        </div>
    );
};

export default TabLockScreen;
